import React, { useEffect, useReducer } from 'react';
import Grid from '@mui/material/Grid';
import { Button } from '@mui/material';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { useRequest } from 'shared/hooks';
import {
  createBuildingDot1x,
  deleteBuildingDot1x,
  getBuildingDot1x,
  updateBuildingDot1x,
} from 'shared/api/buildings';
import { nasEntity, vtrEntity } from 'shared/entityDetails';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { TextEditor } from 'shared/components/data-display/editors';
import UserPrompt from 'shared/components/UserPrompt';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { getNasProfile } from 'shared/api/nases';

const promptMessage = (
  <>
    <p>
      This will delete the Dot1x profile for this building, resulting in removal of
      Zone info as well as Nas/VTR association.
    </p>
    <p>
      <strong>Only continue if you are sure you want to remove both Nas/VTR and Zone info.</strong>
    </p>
    <p>
      Are you sure you want to delete the Dot1x profile for this building?
    </p>
  </>
);

const SetTempDataAction = 'tempUpdate';
const ToggleModalOpen = 'toggleModal';

const nasProfileInitialState = {
  id: undefined,
  nas: undefined,
  nas_id: undefined,
  username: undefined,
};

function reducer(state, {
  action,
  payload,
}) {
  switch (action) {
    case SetTempDataAction:
      return {
        ...state,
        updatedData: { ...state.updatedData, ...payload },
      };
    case ToggleModalOpen:
      return {
        ...state,
        open: !state.open,
      };
    case DataReducerActions.clear:
      return dataReducer({
        ...state,
        data: {},
        open: false,
      }, {
        action,
        payload,
      });
    default:
      return dataReducer(state, {
        action,
        payload,
      });
  }
}

const immutableDescriptions = [
  {
    key: 'username',
    label: 'API Username',
    editor: TextEditor,
  },
  {
    key: 'nas_name',
    editKey: 'nas_id',
    label: 'Nas',
    formatter: (n) => (n.id ? nasEntity.getOptionLabel(n) : ''),
    editor: TextEditor,
  },
];
const mutableDescriptions = [
  {
    key: 'id',
    label: 'VTR',
    editor: withAsyncSelect(vtrEntity),
  },
];

/**
 * @param {Object} props
 * @param {Object} props.building - Building object
 * @param {Function} props.triggerUpdate - Function to trigger update
 * @param {number} props.updateCount - Update counter
 */
export default function VTRPanel({
  building,
  triggerUpdate,
  updateCount,
}) {
  const { request } = useRequest();

  const [state, dispatch] = useReducer(reducer, {
    ...initialDataReducerState,
    data: nasProfileInitialState,
    open: false,
  });

  const {
    auth_method: authMethod,
    dot1x,
    id: buildingId,
  } = building;
  const {
    data: nasProfile,
    dirty,
    open,
    updatedData,
  } = state;

  const {
    id,
    nas,
    username,
  } = nasProfile || {};

  const {
    id: updatedId,
    username: updatedUsername,
    nas: updatedNas,
  } = updatedData;

  useEffect(() => {
    (async () => {
      if (building?.dot1x?.nas_profile_id) {
        if (updatedId && building?.dot1x?.nas_profile?.id === updatedId) {
          dispatch({
            action: DataReducerActions.set,
            payload: building.dot1x.nas_profile,
          });
          return;
        }
      }
      if (building?.dot1x?.nas_profile_id && id !== building?.dot1x?.nas_profile_id) {
        const payload = await request(getNasProfile(building.dot1x.nas_profile_id));
        if (payload) {
          dispatch({
            action: DataReducerActions.set,
            payload,
          });
          return;
        }
      }
      if (!building?.dot1x && id) {
        dispatch({ action: DataReducerActions.clear });
      }
    })();
  }, [building, id, request, updateCount, updatedId]);

  useEffect(() => {
    (async () => {
      if (dirty) {
        let payload;
        if (updatedId === 0) {
          payload = {
            username: nasProfileInitialState.username,
            nas: nasProfileInitialState.nas,
          };
        } else {
          payload = await request(getNasProfile(updatedId));
        }
        if (payload) {
          dispatch({
            action: SetTempDataAction,
            payload,
          });
        }
      }
    })();
  }, [dirty, request, updatedId]);

  const saveDot1xProfile = async (updatedNasProfile) => {
    let promise;
    if (dot1x?.building_id) {
      promise = request(
        updateBuildingDot1x(buildingId, { nas_profile_id: updatedNasProfile.id }),
        'Updated building dot1x with new nas profile',
      );
    } else {
      // Create new dot1x, then load its nas_profile before reloading, so dependent tabs see it.
      promise = request(
        createBuildingDot1x(buildingId, updatedNasProfile.id),
        'Created Dot1xProfile',
      )
        .then(() => request(getBuildingDot1x(buildingId)));
    }
    await promise.then(triggerUpdate);
  };

  const deleteDot1xProfile = async () => {
    await request(
      deleteBuildingDot1x(buildingId),
      'Deleted building nas/vtr and zone info',
    )
      .then(triggerUpdate);
  };

  return (
    <>
      <Grid size={12}>
        <DataFormatter
          descriptions={mutableDescriptions}
          editing={authMethod && authMethod?.includes('802.1x')}
          data={nasProfile}
          updatedData={updatedData}
          updateValue={(key, value) => dispatch({
            action: DataReducerActions.update,
            payload: {
              key,
              value,
            },
          })}
        />
        <DataFormatter
          descriptions={immutableDescriptions}
          data={updatedId !== undefined
            ? { username: updatedUsername, ...updatedNas }
            : { username, ...nas }}
          updatedData={updatedData}
          updateValue={(key, value) => dispatch({
            action: DataReducerActions.update,
            payload: {
              key,
              value,
            },
          })}
        />
      </Grid>
      <Grid
        container
        size={12}
        sx={{
          display: 'flex',
          justifyContent: 'end',
          gap: 1,
          flexWrap: 'wrap-reverse',
          mt: '10px',
        }}
      >
        {dot1x && !dirty ? (
          <Grid>
            <UserPrompt
              isOpen={open}
              message={promptMessage}
              denyCallback={() => dispatch({ action: ToggleModalOpen })}
              acceptBtnText="Delete"
              acceptCallback={deleteDot1xProfile}
              primaryBtnColor={BUTTON_COLORS.error}
            />
            <Button
              variant={VARIANTS.contained}
              color={BUTTON_COLORS.error}
              onClick={() => dispatch({ action: ToggleModalOpen })}
            >
              Delete
            </Button>
          </Grid>
        )
          : (
            <Grid
              container
              spacing={1}
              sx={{
                justifyContent: 'end',
              }}
            >
              <Grid>
                <Button
                  variant={VARIANTS.outlined}
                  disabled={!dirty}
                  onClick={() => dispatch({ action: DataReducerActions.clear })}
                >
                  Clear
                </Button>
              </Grid>
              <Grid>
                <Button
                  disabled={!dirty || updatedId === 0}
                  onClick={() => saveDot1xProfile(updatedData)}
                  variant={VARIANTS.contained}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          )}
      </Grid>
    </>
  );
}
